import React, { useState, useEffect } from 'react';
import { apiClient } from '../services/api';
import { FileText, CheckCircle, AlertCircle, TrendingUp, Clock, MessageCircle, X } from 'lucide-react';
import SummaryChat from './SummaryChat';
import './ConsultationSummaries.css';

interface Client {
  id: string;
  client_name: string;
  client_email: string;
  form_id: string;
  submitted_at: string;
  consultation_forms?: {
    form_title?: string;
    practice_area?: string;
    case_type?: string;
    industry?: string;
  };
}

interface ConsultationSummary {
  id: string;
  form_id: string;
  client_name: string;
  client_email: string;
  summary_text: string;
  status: 'generated' | 'processing' | 'error' | 'failed' | 'completed';
  created_at: string;
  updated_at: string;
  form_data?: any; // Form data from the submission
  summary_json?: any; // Structured assessment data
  lead_quality?: 'Strong' | 'Moderate' | 'Weak'; // Lead quality from database
}

interface ConsultationSummariesProps {
  onSummaryGenerated?: () => void;
  clients?: Client[];
  summaries?: ConsultationSummary[];
  loading?: boolean;
  onRefreshSummaries?: () => void;
}

// Lead type configuration with icons and colors
const LEAD_TYPE_CONFIG: Record<string, { icon: string; label: string; color: string }> = {
  'buying': { icon: '🏠', label: 'Home Purchase', color: '#10b981' },
  'selling': { icon: '📤', label: 'Home Sale', color: '#3b82f6' },
  'landlord': { icon: '📋', label: 'Property Mgmt', color: '#8b5cf6' },
  'tenant': { icon: '👤', label: 'Tenant', color: '#f59e0b' },
  'property_maintenance': { icon: '🔧', label: 'Maintenance', color: '#ef4444' },
  'maintenance_request': { icon: '🔧', label: 'Maintenance', color: '#ef4444' },
  'open_house_buyer': { icon: '🎪', label: 'Open House (Buy)', color: '#06b6d4' },
  'open_house_rental': { icon: '🏢', label: 'Open House (Rent)', color: '#ec4899' },
  'real_estate_transactions': { icon: '🏠', label: 'Real Estate', color: '#10b981' },
  'property_management': { icon: '📋', label: 'Property Mgmt', color: '#8b5cf6' },
  'tenant_services': { icon: '👤', label: 'Tenant', color: '#f59e0b' },
  'immigration': { icon: '✈️', label: 'Immigration', color: '#6366f1' },
  'personal_injury': { icon: '⚖️', label: 'Personal Injury', color: '#dc2626' },
};

const getTypeInfo = (practiceArea?: string, caseType?: string): { icon: string; label: string; color: string } => {
  // First try case_type (more specific), then practice_area
  if (caseType && LEAD_TYPE_CONFIG[caseType]) {
    return LEAD_TYPE_CONFIG[caseType];
  }
  if (practiceArea && LEAD_TYPE_CONFIG[practiceArea]) {
    return LEAD_TYPE_CONFIG[practiceArea];
  }
  return { icon: '📄', label: 'Consultation', color: '#6b7280' };
};

const ConsultationSummaries: React.FC<ConsultationSummariesProps> = ({ 
  onSummaryGenerated,
  clients: propClients = [],
  summaries: propSummaries = [],
  loading: propLoading = false,
  onRefreshSummaries
}) => {
  const [clients, setClients] = useState<Client[]>(propClients);
  const [summaries, setSummaries] = useState<ConsultationSummary[]>(propSummaries);
  const [loading, setLoading] = useState(propLoading);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>(''); // NEW: Type filter
  const [sortBy, setSortBy] = useState<'date' | 'leadQuality' | 'clientName' | 'type'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedSummary, setSelectedSummary] = useState<ConsultationSummary | null>(null);
  const [generatingSummary, setGeneratingSummary] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState<{message: string, type: 'success' | 'error'} | null>(null);
  const [isEditingFormData, setIsEditingFormData] = useState(false);
  const [editedFormData, setEditedFormData] = useState<any>(null);
  const [isFormDataModified, setIsFormDataModified] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showEditTooltip, setShowEditTooltip] = useState(false);
  const [latestCount, setLatestCount] = useState(20);
  const [showChatPanel, setShowChatPanel] = useState(false); 

  // Compute available type options from actual client data (only show types that exist in user's leads)
  const availableTypeOptions = React.useMemo(() => {
    const typesSet = new Set<string>();
    
    clients.forEach(client => {
      const practiceArea = client.consultation_forms?.practice_area;
      const caseType = client.consultation_forms?.case_type;
      
      // Add the most specific type (case_type first, then practice_area)
      if (caseType && LEAD_TYPE_CONFIG[caseType]) {
        typesSet.add(caseType);
      } else if (practiceArea && LEAD_TYPE_CONFIG[practiceArea]) {
        typesSet.add(practiceArea);
      }
    });
    
    // Convert to array with config info and sort by label
    return Array.from(typesSet)
      .map(code => ({
        code,
        ...LEAD_TYPE_CONFIG[code]
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [clients]);

  // Sync with props
  useEffect(() => {
    setClients(propClients);
    setSummaries(propSummaries);
    setLoading(propLoading);
  }, [propClients, propSummaries, propLoading]);

  // Auto-hide toast after 3 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Real-time status polling for processing summaries
  useEffect(() => {
    const processingSummaries = summaries.filter(s => s.status === 'processing');
    
    if (processingSummaries.length === 0) {
      return; // No processing summaries, no need to poll
    }

    console.log(`🔄 Polling ${processingSummaries.length} processing summaries...`);

    const pollInterval = setInterval(async () => {
      try {
        // Fetch updated summaries for processing forms
        const formIds = processingSummaries.map(s => s.form_id);
        const promises = formIds.map(formId => 
          apiClient.get(`/consultation-forms/${formId}/summary`).catch(() => null)
        );
        
        const results = await Promise.all(promises);
        
        // Update summaries with new status and track completion
        let summaryJustCompleted = false;
        
        setSummaries(prevSummaries => {
          let hasUpdates = false;
          const updatedSummaries = prevSummaries.map(summary => {
            const result = results.find(r => r && r.form_id === summary.form_id);
            if (result && result.status !== summary.status) {
              hasUpdates = true;
              console.log(`📊 Status updated for ${summary.client_name}: ${summary.status} → ${result.status}`);
              
              // Check if summary just completed (processing → generated)
              if (summary.status === 'processing' && result.status === 'generated') {
                summaryJustCompleted = true;
                console.log('✅ Summary completed via polling, will refresh usage count');
              }
              
              return { ...summary, ...result };
            }
            return summary;
          });

          if (hasUpdates) {
            console.log('✅ Summary statuses updated via polling');
          }

          return updatedSummaries;
        });

        // Refresh usage count if a summary just completed
        if (summaryJustCompleted && onSummaryGenerated) {
          console.log('📊 Calling onSummaryGenerated to refresh usage count');
          onSummaryGenerated();
        }

      } catch (error) {
        console.error('❌ Error polling summary statuses:', error);
      }
    }, 3000); // Poll every 3 seconds

    return () => {
      clearInterval(pollInterval);
      console.log('🛑 Stopped polling summary statuses');
    };
  }, [summaries.filter(s => s.status === 'processing').length, onSummaryGenerated]); // Re-run when processing count changes or callback changes

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
  };


  const handleGenerateSummary = async (formId: string, clientName: string) => {
    try {
      setGeneratingSummary(formId);
      const response = await apiClient.post(`/consultation-forms/${formId}/generate-summary`);
      
      if (response.success) {
        // Fetch the just-generated summary and merge into local state only
        const summaryResponse = await apiClient.get(`/consultation-forms/${formId}/summary`);

        // Ensure form_data is available: if API doesn't include it, fetch latest submission data
        if (summaryResponse && !summaryResponse.form_data) {
          try {
            const submissions = await apiClient.get(`/consultation-forms/${formId}/submissions`);
            if (Array.isArray(submissions) && submissions.length > 0) {
              const latest = submissions.sort((a: any, b: any) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime())[0];
              summaryResponse.form_data = latest?.form_data || null;
            }
          } catch (e) {
            console.warn('⚠️ Unable to attach form_data to summary:', e);
          }
        }

        if (summaryResponse) {
          setSummaries(prev => {
            const exists = prev.some(s => s.form_id === formId);
            if (exists) {
              return prev.map(s => s.form_id === formId ? { ...s, ...summaryResponse } : s);
            }
            return [{ ...summaryResponse }, ...prev];
          });
        }
        const action = response.isRegeneration ? 'regenerated' : 'generated';
        showToast(`Summary ${action} successfully for ${clientName}`, 'success');
        // Notify parent about usage only; avoid refetching all summaries
        if (onSummaryGenerated) onSummaryGenerated();
      } else {
        const detail = response.message ? `: ${response.message}` : '';
        showToast(`Failed to generate summary${detail}`, 'error');
      }
    } catch (error) {
      console.error('Error generating summary:', error);
      const apiError = error as any;
      const status = apiError?.response?.status;
      const data = apiError?.response?.data || {};
      const serverMessage = data?.message || apiError?.message || 'Failed to generate summary.';
      const usageDetails = typeof data?.current === 'number' && typeof data?.limit === 'number'
        ? ` (${data.current}/${data.limit} summaries used)`
        : '';

      if (status === 403) {
        try {
          showToast(`${serverMessage}${usageDetails}`, 'error');
        } catch (toastError) {
          console.warn('⚠️ Toast component unavailable, falling back to alert.');
          alert(`${serverMessage}${usageDetails}`);
        }
      } else {
        try {
          showToast(serverMessage, 'error');
        } catch (toastError) {
          console.warn('⚠️ Toast component unavailable, falling back to alert.');
          alert(serverMessage);
        }
      }
    } finally {
      setGeneratingSummary(null);
    }
  };

  const handleViewSummary = (summary: ConsultationSummary) => {
    console.log('📊 Opening summary with data:', summary);
    console.log('📝 Form data:', summary.form_data);
    setSelectedSummary(summary);
    setEditedFormData(summary.form_data ? { ...summary.form_data } : null);
    // If source data is missing, fetch it on-demand (latest submission)
    if (!summary.form_data) {
      (async () => {
        try {
          const submissions = await apiClient.get(`/consultation-forms/${summary.form_id}/submissions`);
          if (Array.isArray(submissions) && submissions.length > 0) {
            const latest = submissions.sort((a: any, b: any) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime())[0];
            const updated = { ...summary, form_data: latest?.form_data || null } as ConsultationSummary;
            setSelectedSummary(updated);
            setEditedFormData(updated.form_data ? { ...updated.form_data } : null);
            // Also sync into summaries list
            setSummaries(prev => prev.map(s => s.form_id === summary.form_id ? { ...s, form_data: updated.form_data } : s));
          }
        } catch (e) {
          console.warn('⚠️ Unable to fetch source form_data for summary:', e);
        }
      })();
    }
    setIsEditingFormData(false);
    setIsFormDataModified(false);
    setShowModal(true);
  };

  const handleEditFormData = () => {
    setIsEditingFormData(true);
  };

  const handleCancelEdit = () => {
    setIsEditingFormData(false);
    setShowEditTooltip(false); // Hide tooltip when canceling
    setEditedFormData(selectedSummary?.form_data ? { ...selectedSummary.form_data } : null);
    setIsFormDataModified(false);
  };

  const handleFormDataChange = (key: string, value: string) => {
    setEditedFormData((prev: any) => ({
      ...prev,
      [key]: value
    }));
    setIsFormDataModified(true);
  };

  const handleSaveFormData = async () => {
    if (!selectedSummary || !editedFormData) return;
    
    try {
      setIsSaving(true);
      
      // Update the form_data in the consultation_form_submissions table
      await apiClient.put(`/consultation-forms/submissions/${selectedSummary.form_id}/form-data`, {
        form_data: editedFormData
      });
      
      // Update local state
      setSelectedSummary({
        ...selectedSummary,
        form_data: editedFormData
      });
      
      setIsEditingFormData(false);
      setShowEditTooltip(false); // Hide tooltip after saving
      showToast('Form data updated successfully', 'success');
    } catch (error) {
      console.error('Error saving form data:', error);
      showToast('Failed to save form data', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRegenerateSummary = async () => {
    if (!selectedSummary) return;
    
    try {
      setGeneratingSummary(selectedSummary.form_id);
      const startTime = Date.now();
      
      const response = await apiClient.post(`/consultation-forms/${selectedSummary.form_id}/generate-summary`);
      
      const elapsedTime = Date.now() - startTime;
      console.log(`⏱️ Summary regeneration took ${elapsedTime}ms`);
      console.log('📊 Data changed:', response.dataChanged);
      
      if (response.success) {
        // Fetch the updated summary directly from the API
        const summaryResponse = await apiClient.get(`/consultation-forms/${selectedSummary.form_id}/summary`);
        
        if (summaryResponse) {
          // Update the selected summary with the fresh data from API
          const updatedSummary = {
            ...selectedSummary,
            summary_text: summaryResponse.summary_text,
            status: summaryResponse.status,
            updated_at: summaryResponse.updated_at,
            summary_json: summaryResponse.summary_json,
            lead_quality: summaryResponse.lead_quality
          };
          
          setSelectedSummary(updatedSummary);
          
          // Update the summaries list with the new summary data
          setSummaries(prev => prev.map(s => 
            s.form_id === selectedSummary.form_id ? { ...s, ...summaryResponse } : s
          ));
        }
        
        // Always show success message
        showToast('Summary has been generated', 'success');
        
        setIsFormDataModified(false);
        // Modal stays open to show the new summary
      } else {
        showToast('Failed to regenerate summary', 'error');
      }
    } catch (error) {
      console.error('Error regenerating summary:', error);
      showToast('Failed to regenerate summary', 'error');
    } finally {
      setGeneratingSummary(null);
    }
  };


  const getSummaryStatus = (formId: string): 'ready' | 'not started' | 'processing' | 'failed' => {
    const summary = summaries.find(s => s.form_id === formId);
    if (!summary) return 'not started';
    const normalizedStatus = summary.status === 'completed' ? 'generated' : summary.status;
    
    if (normalizedStatus === 'processing') {
      const updatedAt = new Date(summary.updated_at);
      const now = new Date();
      const minutesSinceUpdate = (now.getTime() - updatedAt.getTime()) / 1000 / 60;
      
      if (minutesSinceUpdate > 4) {
        console.log(`⚠️ Summary generation timed out for ${formId} (${minutesSinceUpdate.toFixed(1)} minutes)`);
        return 'failed';
      }
      return 'processing';
    }
    
    if (normalizedStatus === 'generated') return 'ready';
    if (normalizedStatus === 'error' || normalizedStatus === 'failed') return 'failed';
    return 'not started';
  };

  const getSummaryForClient = (formId: string): ConsultationSummary | undefined => {
    return summaries.find(s => s.form_id === formId && (s.status === 'generated' || s.status === 'completed'));
  };

  // Helper function to format summary text and replace text separators with HR elements
  const formatSummaryText = (text: string) => {
    // Split by lines that are mostly separator characters (═, =, -, _)
    const lines = text.split('\n');
    const formattedLines: (string | React.ReactElement)[] = [];
    
    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      // Check if line is mostly separator characters (at least 10 consecutive separator chars)
      const isSeparatorLine = /^[═=\-_]{10,}$/.test(trimmedLine);
      
      if (isSeparatorLine) {
        // Replace with a single HR element
        formattedLines.push(<hr key={`hr-${index}`} style={{ 
          border: 'none', 
          borderTop: '2px solid #e5e7eb', 
          margin: '1rem 0',
          width: '100%'
        }} />);
      } else if (line) {
        formattedLines.push(line + '\n');
      } else {
        formattedLines.push('\n');
      }
    });
    
    return formattedLines;
  };

  // Helper function to render form data values in a readable format
  const renderFormDataValue = (value: any): React.ReactNode => {
    // Handle null, undefined, or empty string
    if (value === null || value === undefined || value === '') {
      return <span className="empty-value">Not provided</span>;
    }
    
    // Handle objects (structured data like IELTS, Education, Work tables)
    if (typeof value === 'object' && !Array.isArray(value)) {
      // Check if all values in the object are null/empty
      const hasAnyValue = Object.values(value).some(v => v !== null && v !== undefined && v !== '');
      
      if (!hasAnyValue) {
        return <span className="empty-value">Not provided</span>;
      }
      
      // Render structured data with proper layout using table for guaranteed horizontal display
      return (
        <table className="structured-data-table">
          <tbody>
            {Object.entries(value).map(([subKey, subValue]) => {
              const labelText = subKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              const valueText = subValue !== null && subValue !== undefined && subValue !== '' 
                ? String(subValue) 
                : 'Not provided';
              
              return (
                <tr key={subKey} className="structured-data-row">
                  <td className="sub-label">
                    <span style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
                      {labelText}:
                    </span>
                  </td>
                  <td className="sub-value">
                    <span style={{ display: 'inline-block', whiteSpace: 'normal' }}>
                      {valueText}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      );
    }
    
    // Handle simple values (strings, numbers)
    return <span className="simple-value">{String(value)}</span>;
  };

  // Helper function to render editable form data
  const renderEditableFormData = (fieldKey: string, value: any): React.ReactNode => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      // Simple text input for non-structured data
      return (
        <input
          type="text"
          className="form-data-input"
          value={String(value || '')}
          onChange={(e) => handleFormDataChange(fieldKey, e.target.value)}
        />
      );
    }
    
    // Structured data - show editable sub-fields
    return (
      <div className="structured-data-edit">
        {Object.entries(value).map(([subKey, subValue]) => (
          <div key={subKey} className="structured-data-edit-row">
            <label className="sub-label-edit">{subKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</label>
            <input
              type="text"
              className="sub-field-input"
              value={String(subValue || '')}
              onChange={(e) => {
                const updatedValue = {
                  ...value,
                  [subKey]: e.target.value
                };
                handleFormDataChange(fieldKey, updatedValue);
              }}
              placeholder={`Enter ${subKey.replace(/_/g, ' ')}`}
            />
          </div>
        ))}
      </div>
    );
  };

  // Helper function to format date for display
  const formatSubmittedDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get lead quality for a client
  const getClientLeadQuality = (formId: string): 'Strong' | 'Moderate' | 'Weak' | null => {
    const summary = summaries.find(s => s.form_id === formId);
    if (summary?.lead_quality) {
      return summary.lead_quality;
    }
    if (summary?.summary_json?.leadQuality) {
      return summary.summary_json.leadQuality;
    }
    return null;
  };

  // Filter clients by search term and date range
  const filteredClients = clients.filter(client => {
    // Text search filter
    const matchesSearch = 
      client.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.client_email.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;

    // Type filter
    if (typeFilter) {
      const clientPracticeArea = client.consultation_forms?.practice_area || '';
      const clientCaseType = client.consultation_forms?.case_type || '';
      if (clientPracticeArea !== typeFilter && clientCaseType !== typeFilter) {
        return false;
      }
    }

    // Date range filter
    if (dateFrom || dateTo) {
      const submittedDate = new Date(client.submitted_at);
      submittedDate.setHours(0, 0, 0, 0); // Reset time to start of day for comparison
      
      if (dateFrom) {
        const fromDate = new Date(dateFrom);
        fromDate.setHours(0, 0, 0, 0);
        if (submittedDate < fromDate) return false;
      }
      
      if (dateTo) {
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999); // End of day
        if (submittedDate > toDate) return false;
      }
    }
    
    return true;
  });

  // Sort filtered clients
  const sortedClients = [...filteredClients].sort((a, b) => {
    let comparison = 0;

    if (sortBy === 'date') {
      comparison = new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime();
    } else if (sortBy === 'clientName') {
      comparison = a.client_name.localeCompare(b.client_name);
    } else if (sortBy === 'type') {
      const typeA = getTypeInfo(a.consultation_forms?.practice_area, a.consultation_forms?.case_type).label;
      const typeB = getTypeInfo(b.consultation_forms?.practice_area, b.consultation_forms?.case_type).label;
      comparison = typeA.localeCompare(typeB);
      
      // If same type, sort by date as secondary
      if (comparison === 0) {
        comparison = new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime();
      }
    } else if (sortBy === 'leadQuality') {
      const qualityA = getClientLeadQuality(a.form_id);
      const qualityB = getClientLeadQuality(b.form_id);
      
      // Define quality order: Strong (3) > Moderate (2) > Weak (1) > null (0)
      const qualityOrder: Record<string, number> = {
        'Strong': 3,
        'Moderate': 2,
        'Weak': 1
      };
      
      const orderA = qualityOrder[qualityA || ''] || 0;
      const orderB = qualityOrder[qualityB || ''] || 0;
      
      comparison = orderB - orderA; // Higher quality first
      
      // If same quality, sort by date as secondary
      if (comparison === 0) {
        comparison = new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime();
      }
    }

    return sortOrder === 'asc' ? -comparison : comparison;
  });

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading consultation summaries...</p>
      </div>
    );
  }

  return (
    <div className="consultation-summaries">
      <div className="search-section">
        <div className="search-inputs-row">
          <input
            type="text"
            placeholder="Search by client name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <div className="date-filters">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="type-filter-select"
              title="Filter by type"
            >
              <option value="">All Types</option>
              {availableTypeOptions.map(type => (
                <option key={type.code} value={type.code}>
                  {type.icon} {type.label}
                </option>
              ))}
            </select>
            <input
              type="date"
              placeholder="From Date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="date-input"
              title="Filter from date"
            />
            <input
              type="date"
              placeholder="To Date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="date-input"
              title="Filter to date"
            />
            {(dateFrom || dateTo || typeFilter) && (
              <button
                onClick={() => {
                  setDateFrom('');
                  setDateTo('');
                  setTypeFilter('');
                }}
                className="clear-date-btn"
                title="Clear filters"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="latest-slider-container">
        <div className="slider-controls-left">
          <label htmlFor="latest-slider">Show latest {latestCount} forms:</label>
          <input
            type="range"
            id="latest-slider"
            min={1}
            max={clients.length > 0 ? clients.length : 1}
            value={latestCount}
            onChange={(e) => setLatestCount(Number(e.target.value))}
          />
        </div>
        <div className="sort-controls">
          <label htmlFor="sort-by">Sort by:</label>
          <select
            id="sort-by"
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value as 'date' | 'leadQuality' | 'clientName' | 'type');
              // Set default sort order based on sort type
              if (e.target.value === 'leadQuality') {
                setSortOrder('desc'); // Strong first
              } else if (e.target.value === 'clientName' || e.target.value === 'type') {
                setSortOrder('asc'); // A-Z
              } else {
                setSortOrder('desc'); // Newest first
              }
            }}
            className="sort-select"
          >
            <option value="date">Date Submitted</option>
            <option value="type">Type</option>
            <option value="leadQuality">Lead Quality</option>
            <option value="clientName">Client Name</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="sort-order-btn"
            title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      {filteredClients.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📝</div>
          <h3>No consultation summaries found</h3>
          <p>Generate summaries from your consultation form submissions</p>
        </div>
      ) : (
        <div className="summaries-table-container">
          <table className="summaries-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Client Name</th>
                <th>Email</th>
                <th>Data Submitted</th>
                <th>Lead Quality</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedClients.slice(0, latestCount).map(client => {
                const status = getSummaryStatus(client.form_id);
                const summary = getSummaryForClient(client.form_id);
                const isGenerating = generatingSummary === client.form_id;
                const leadQuality = getClientLeadQuality(client.form_id);
                const typeInfo = getTypeInfo(client.consultation_forms?.practice_area, client.consultation_forms?.case_type);
                
                return (
                  <tr key={client.id}>
                    <td className="lead-type-cell">
                      <span 
                        className="lead-type-badge" 
                        style={{ backgroundColor: `${typeInfo.color}15`, color: typeInfo.color, borderColor: `${typeInfo.color}30` }}
                      >
                        <span className="type-icon">{typeInfo.icon}</span>
                        <span className="type-label">{typeInfo.label}</span>
                      </span>
                    </td>
                    <td className="client-name">{client.client_name}</td>
                    <td className="client-email">{client.client_email}</td>
                    <td className="data-submitted">
                      <div className="submitted-date">
                        {formatSubmittedDate(client.submitted_at)}
                      </div>
                      {status !== 'ready' && (
                        <div className="status-indicator-inline">
                          <span className={`status-badge-small ${status.replace(' ', '-')}`}>
                            {isGenerating ? 'Generating...' : status === 'not started' ? 'No Summary' : status.toUpperCase()}
                            {status === 'processing' && (
                              <span className="pulse-dot" title="Status updating in real-time">●</span>
                            )}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="lead-quality-cell">
                      {leadQuality ? (
                        <span className={`lead-quality-badge-table ${leadQuality.toLowerCase()}`}>
                          {leadQuality}
                        </span>
                      ) : (
                        <span className="lead-quality-empty">—</span>
                      )}
                    </td>
                    <td className="actions">
                      {status === 'ready' && summary ? (
                        <button 
                          onClick={() => handleViewSummary(summary)}
                          className="view-btn"
                        >
                          View
                        </button>
                      ) : status === 'not started' || status === 'failed' ? (
                        <button 
                          onClick={() => handleGenerateSummary(client.form_id, client.client_name)}
                          disabled={isGenerating}
                          className={status === 'failed' ? 'regenerate-btn' : 'generate-btn'}
                        >
                          {isGenerating ? 'Generating...' : status === 'failed' ? 'Retry Generation' : 'Generate Summary'}
                        </button>
                      ) : (
                        <span className="processing-text">Processing...</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modern Summary Modal */}
      {showModal && selectedSummary && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modern-summary-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header Section */}
            <div className="modern-modal-header">
              <div className="header-content">
                <div className="header-left">
                  <div className="header-icon">
                    <FileText size={20} />
                  </div>
                  <div>
                    <div className="header-subtitle">Eligibility Analysis Report</div>
                    <h1 className="header-title">{selectedSummary.client_name}</h1>
                    <p className="header-description">Initial Consultation Summary & Analysis</p>
                  </div>
                </div>
                <button 
                  className="modern-close-btn"
                  onClick={() => setShowModal(false)}
                  title="Close"
                >
                  ×
                </button>
              </div>
              
              <div className="header-meta">
                <div className="meta-item">
                  <div className="meta-label">Email</div>
                  <div className="meta-value">{selectedSummary.client_email}</div>
                </div>
                <div className="meta-item">
                  <div className="meta-label">Generated</div>
                  <div className="meta-value">{new Date(selectedSummary.created_at).toLocaleDateString()}</div>
                </div>
                {selectedSummary.updated_at && selectedSummary.updated_at !== selectedSummary.created_at && (
                  <div className="meta-item">
                    <div className="meta-label">Last Updated</div>
                    <div className="meta-value">{new Date(selectedSummary.updated_at).toLocaleDateString()}</div>
                  </div>
                )}
                {selectedSummary.lead_quality && (
                  <div className="meta-item">
                    <div className="meta-label">Lead Quality</div>
                    <div className={`meta-value lead-quality-badge ${selectedSummary.lead_quality.toLowerCase()}`}>
                      {selectedSummary.lead_quality}
                    </div>
                  </div>
                )}
                {selectedSummary.summary_json?.leadScore !== undefined && (
                  <div className="meta-item">
                    <div className="meta-label">Lead Score</div>
                    <div className="meta-value lead-score-value">
                      {selectedSummary.summary_json.leadScore}/100
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="action-bar">
              <button 
                className="action-btn regenerate"
                onClick={handleRegenerateSummary}
                disabled={!!generatingSummary}
                title="Regenerate summary with current form data"
              >
                <TrendingUp size={18} />
                <span>{generatingSummary ? 'Regenerating...' : 'Regenerate'}</span>
              </button>
            </div>

            {/* Split-Screen Content Area */}
            <div className="modern-modal-body split-view">
              {/* Left Panel: AI Summary */}
              <div className="split-panel left-panel">
                <div className="panel-header">
                  <h3>Summary</h3>
                </div>
                <div className="panel-content">
                  {generatingSummary === selectedSummary.form_id ? (
                    <div className="summary-loading">
                      <div className="loading-spinner"></div>
                      <p>Regenerating summary with AI...</p>
                      <p className="loading-subtext">This may take 10-30 seconds</p>
                    </div>
                  ) : (
                    <div className="modern-summary-content">
                      <div className="summary-text-modern">
                        {formatSummaryText(selectedSummary.summary_text)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Right Panel: Source Data */}
              <div className="split-panel right-panel">
                <div className="panel-header">
                  <h3>Source Data</h3>
                  <div className="form-data-actions">
                    {!isEditingFormData ? (
                      <button 
                        className="edit-form-data-btn"
                        onClick={handleEditFormData}
                      >
                        ✏️ Edit Data
                      </button>
                    ) : (
                      <>
                        <button 
                          className="save-form-data-btn"
                          onClick={handleSaveFormData}
                          disabled={isSaving}
                        >
                          {isSaving ? 'Saving...' : '💾 Save'}
                        </button>
                <button 
                          className="cancel-edit-btn"
                          onClick={handleCancelEdit}
                >
                          ✖️ Cancel
                </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="panel-content">
                  {editedFormData && Object.keys(editedFormData).length > 0 ? (
                    <>
                      <div className="form-data-grid">
                        {Object.entries(editedFormData).map(([key, value]) => (
                          <div key={key} className="form-data-card">
                            <div className="form-data-label-modern">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
                            {isEditingFormData ? (
                              renderEditableFormData(key, value)
                            ) : (
                              <div className="form-data-value-modern">
                                {renderFormDataValue(value)}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      {isFormDataModified && !isEditingFormData && (
                        <div className="regenerate-prompt">
                          <AlertCircle size={20} />
                          <div>
                            <p>Form data has been modified</p>
                <button 
                              className="regenerate-summary-btn"
                              onClick={handleRegenerateSummary}
                              disabled={!!generatingSummary}
                            >
                              {generatingSummary ? 'Regenerating...' : 'Regenerate Summary'}
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="no-data">
                      <p>No source data available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating Chat Button - Only show when modal is open */}
      {showModal && selectedSummary && (
        <button 
          className="floating-chat-btn"
          onClick={() => setShowChatPanel(true)}
          title="Chat about this assessment"
        >
          <MessageCircle size={24} />
        </button>
      )}

      {/* Chat Side Panel */}
      {showChatPanel && selectedSummary && (
        <div className="chat-side-panel-overlay" onClick={() => setShowChatPanel(false)}>
          <div className="chat-side-panel" onClick={(e) => e.stopPropagation()}>
            <div className="chat-panel-header">
              <div className="chat-panel-title">
                <MessageCircle size={20} />
                <span>Chat about {selectedSummary.client_name}'s Assessment</span>
              </div>
              <button 
                className="chat-panel-close"
                onClick={() => setShowChatPanel(false)}
                title="Close Chat"
              >
                <X size={20} />
              </button>
            </div>
            <div className="chat-panel-content">
              <SummaryChat 
                summaryText={selectedSummary.summary_text}
                clientName={selectedSummary.client_name}
                clientEmail={selectedSummary.client_email}
                summaryId={selectedSummary.id}
                onClose={() => setShowChatPanel(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className={`toast-notification ${toast.type}`}>
          <div className="toast-content">
            <span className="toast-icon">
              {toast.type === 'success' ? '✅' : '❌'}
            </span>
            <span className="toast-message">{toast.message}</span>
            <button 
              className="toast-close"
              onClick={() => setToast(null)}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultationSummaries;
